import React, { useState } from 'react';
import { 
  Lightbulb, Download, Copy, Check, 
  AlertCircle, ArrowLeft, Sparkles, FileText, HelpCircle 
} from 'lucide-react';

interface StartupPitchBuilderProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const LOADING_STEPS_UK = [
  "Ініціалізація модуля оцінки стартапу...",
  "Валідатор аналізує життєздатність та відповідність ринку (Product-Market Fit)...",
  "Венчурний аналітик розробляє послайдову структуру Pitch Deck...",
  "Фандрейзинг-стратег будує One-Pager та дорожню карту залучення капіталу...",
  "Генерація фінального інвестиційного звіту стартапу..."
];

const LOADING_STEPS_EN = [
  "Initializing startup pitch AI agents...",
  "Startup Validator evaluates viability and Product-Market Fit...",
  "Venture Analyst designs the slide-by-slide Pitch Deck structure...",
  "Fundraising Strategist structures the One-Pager and funding roadmap...",
  "Generating final startup investment report..."
];

export default function StartupPitchBuilder({ language, onBack, onGoHome, useEnterpriseContext, enterpriseName }: StartupPitchBuilderProps) {
  const isUk = language === 'uk';
  const [startupIdea, setStartupIdea] = useState('');
  const [productDetails, setProductDetails] = useState('');
  const [fundingNeeds, setFundingNeeds] = useState('');
  
  const [isAuditing, setIsAuditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<string | null>(null);
  
  const [copiedReport, setCopiedReport] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);

  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAuditing) {
      setLoadingStepIndex(0);
      const steps = isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN;
      interval = setInterval(() => {
        setLoadingStepIndex((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 7000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuditing, isUk]);

  const handleRun = async () => {
    if (!startupIdea.trim() || !productDetails.trim() || !fundingNeeds.trim()) return;

    setIsAuditing(true);
    setError(null);
    setReport(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const response = await fetch('/api/run-templated-crew', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          crewName: "Валідація стартапів та підготовка до інвестицій",
          inputs: {
            startup_idea: startupIdea,
            product_details: productDetails,
            funding_needs: fundingNeeds
          },
          useEnterpriseContext
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isUk ? 'Помилка генерації стартап-звіту.' : 'Failed to generate startup report.'));
      }

            const data = await response.json();
      if (data.jobId) {
        const pollJob = async () => {
          try {
            const statusRes = await fetch(`/api/crew-status/${data.jobId}`);
            if (!statusRes.ok) {
              throw new Error(isUk ? 'Помилка перевірки статусу завдання.' : 'Status check failed.');
            }
            const statusData = await statusRes.json();
            if (statusData.status === 'completed') {
              const rawReport = statusData.report || statusData.result || '';
              const resText = typeof rawReport === 'string'
                ? rawReport
                : (typeof rawReport === 'object' && rawReport !== null
                    ? (rawReport.report || rawReport.result || JSON.stringify(rawReport, null, 2))
                    : String(rawReport));

              if (!resText) {
                throw new Error(isUk ? 'Сервер повернув порожній результат звіту.' : 'Server returned empty report content.');
              }
              setReport(resText);
              setIsAuditing(false);
            } else if (statusData.status === 'failed') {
              setError(statusData.error || (isUk ? 'Помилка генерації звіту.' : 'Report generation failed.'));
              setIsAuditing(false);
            } else {
              setTimeout(pollJob, 2000);
            }
          } catch (pollErr: any) {
            console.error(pollErr);
            setError(pollErr.message || (isUk ? 'Помилка опитування завдання.' : 'Polling error.'));
            setIsAuditing(false);
          }
        };
        setTimeout(pollJob, 1000);
      } else {
        setReport(data.report || data.result || '');
        setIsAuditing(false);
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Сталася помилка.' : 'An error occurred.'));
      setIsAuditing(false);
    }
  };

  const handleDownloadWord = async () => {
    if (!report) return;

    try {
      const name = "Startup_Investment_Pitch_" + startupIdea.replace(/[^a-zA-Z0-9А-Яа-яЄєІіЇїҐґ_-]/g, "_");
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: report,
          fileName: name
        })
      });

      if (!response.ok) throw new Error(isUk ? 'Не вдалося згенерувати Word файл.' : 'Failed to generate Word file.');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error(err);
      alert(err.message);
    }
  };

  const handleCopyReport = () => {
    if (!report) return;
    navigator.clipboard.writeText(report);
    setCopiedReport(true);
    setTimeout(() => setCopiedReport(false), 2000);
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-4 pb-12 text-slate-800">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={onGoHome || onBack}
              className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
            >
              🏠 {isUk ? "На головну" : "Home"}
            </button>
            <button
              onClick={onBack}
              className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
            >
              <ArrowLeft className="w-4 h-4 text-blue-600" />
              <span>{isUk ? "← До Бізнес Центру" : "← Back to Business Hub"}</span>
            </button>
          </div>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-amber-500" />
              <span>{isUk ? "Оцінка Стартапів & Інвестиційний Питч" : "AI Startup Mentor & Pitch Builder"}</span>
            </h1>
            <p className="text-xs text-slate-500">
              {isUk ? "Валідація бізнес-ідей, підготовка покрокової структури Pitch Deck, One-Pager та дорожньої карти залучення інвестицій" : "Evaluates startup ideas, designs Pitch Decks structures, Executive Summaries, and fundraising roadmaps"}
            </p>
          </div>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/30 px-3 py-1 rounded-full flex items-center gap-1">
          <Sparkles className="w-3.5 h-3.5 text-amber-600 animate-pulse" />
          <span className="text-[10px] text-amber-600 font-bold uppercase tracking-wider">Multi-Agent AI</span>
        </div>
      </div>

      {useEnterpriseContext && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3.5 flex items-center justify-between text-xs text-amber-800">
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-amber-600 animate-pulse" />
            <div>
              <span className="font-bold">{isUk ? "Активовано контекст підприємства: " : "Enterprise Context Active: "}</span>
              <span className="font-mono">{enterpriseName || (isUk ? "База знань компанії" : "Company Knowledge Base")}</span>
            </div>
          </div>
          <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 rounded text-amber-700 font-bold uppercase tracking-wider">
            {isUk ? "База знань" : "Knowledge Base"}
          </span>
        </div>
      )}

      {/* Main Form */}
      {!report && !isAuditing && (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => {
              setStartupIdea("Antigravity AI Logistics — Автономний ШІ-диспетчер вантажних перевезень в ЄС");
              setProductDetails("B2B SaaS платформа для оптимізації маршрутів вантажівок та автопарків. Зменшує холостий пробіг на 25% за допомогою алгоритмів машинного навчання. Монетизація: підписка 49 EUR/міс за кожну вантажівку.");
              setFundingNeeds("Потрібно $200 000 Pre-Seed інвестицій на 12 місяців для розширення команди розробки та виходу на ринки Польщі та Німеччини.");
              setError(null);
            }}
            className="w-full py-2.5 px-4 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl text-emerald-800 font-mono text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 shadow-2xs"
          >
            <Sparkles className="w-4 h-4 text-emerald-600 animate-pulse" />
            <span>{isUk ? "⚡ Спробувати на демо-прикладі в 1 клік (Pitch Deck стартапу Antigravity AI)" : "⚡ Try 1-click Demo Sample (Startup Pitch Deck)"}</span>
          </button>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-xs">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {isUk ? "Назва та ключова ідея стартапу" : "Startup Name & Key Idea"}
                </label>
                <input
                  type="text"
                  placeholder={isUk 
                    ? "Наприклад: EcoPack — сервіс оренди та переробки багаторазової упаковки для ресторанів" 
                    : "Example: EcoPack — reusable packaging rental and recycling for restaurants"}
                  value={startupIdea}
                  onChange={(e) => setStartupIdea(e.target.value)}
                  className="w-full p-2.5 bg-slate-55/10 border border-slate-200 rounded-lg focus:border-amber-500 focus:outline-none text-sm transition-colors text-slate-850"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {isUk ? "Деталі продукту / технологія та бізнес-модель" : "Product details / tech stack & business model"}
                </label>
                <textarea
                  placeholder={isUk 
                    ? "Опишіть, як працює ваш продукт, які технології використовуються, та як стартап планує заробляти гроші (підписка, транзакційна модель, B2B продаж тощо)..." 
                    : "Describe how your product works, tech stack, and monetization model (subscription, commission, B2B sales)..."}
                  value={productDetails}
                  onChange={(e) => setProductDetails(e.target.value)}
                  rows={5}
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 text-sm focus:outline-none transition-colors placeholder-slate-400 leading-relaxed font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {isUk ? "Потреби у фінансуванні та цілі (на що потрібні кошти)" : "Funding needs & goals (target use of funds)"}
                </label>
                <textarea
                  placeholder={isUk 
                    ? "Наприклад: Потрібно $150,000 на розробку MVP (6 місяців), маркетинг та перші 100 B2B клієнтів..." 
                    : "Example: Need $150,000 for MVP development (6 months), marketing, and acquiring first 100 B2B clients..."}
                  value={fundingNeeds}
                  onChange={(e) => setFundingNeeds(e.target.value)}
                  rows={3}
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 text-sm focus:outline-none transition-colors placeholder-slate-400 leading-relaxed font-mono"
                />
              </div>
            </div>

            <button
              onClick={handleRun}
              disabled={!startupIdea.trim() || !productDetails.trim() || !fundingNeeds.trim()}
              className="w-full py-3 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-100 disabled:text-slate-400 text-white font-black text-sm uppercase rounded-xl tracking-wider flex items-center justify-center gap-2 transition-colors cursor-pointer shadow-xs"
            >
              <Lightbulb className="w-4 h-4" />
              <span>{isUk ? "Проаналізувати стартап" : "Analyze Startup"}</span>
            </button>
          </div>

          {/* Guidelines Sidebar */}
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-200 pb-2">
                <HelpCircle className="w-4 h-4 text-amber-500" />
                <span>{isUk ? "З чим допоможе агент?" : "How this agent helps?"}</span>
              </h3>
              <ul className="space-y-3 font-mono text-[10px] text-slate-650 leading-relaxed">
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-amber-100 text-amber-750 font-bold rounded-full flex items-center justify-center shrink-0">1</span>
                  <span>{isUk ? "Валідатор оцінює ризики, ринковий потенціал та конкурентоспроможність." : "Validator screens feasibility, market size, and competitiveness."}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-amber-100 text-amber-750 font-bold rounded-full flex items-center justify-center shrink-0">2</span>
                  <span>{isUk ? "Складає послайдовий план Pitch Deck з коментарями, що писати на кожному слайді." : "Structures a slide-by-slide Pitch Deck map with comments for each slide."}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-amber-100 text-amber-750 font-bold rounded-full flex items-center justify-center shrink-0">3</span>
                  <span>{isUk ? "Формує готовий інвестиційний One-Pager та дорожню карту фінансування." : "Creates investment One-Pager and tailored funding roadmaps."}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Loading State */}
      {isAuditing && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-xs flex flex-col items-center justify-center min-h-[350px] space-y-6">
          <div className="relative w-16 h-16 flex items-center justify-center">
            <div className="absolute w-full h-full border-4 border-amber-200 rounded-full animate-pulse" />
            <div className="absolute w-full h-full border-4 border-t-amber-550 rounded-full animate-spin" />
            <Lightbulb className="w-6 h-6 text-amber-500" />
          </div>
          <div className="text-center space-y-2 max-w-md">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-800">
              {isUk ? "Аналіз стартап-проєкту..." : "Analyzing startup by AI..."}
            </h3>
            <p className="text-xs text-slate-500 font-mono italic leading-normal px-4">
              {isUk ? LOADING_STEPS_UK[loadingStepIndex] : LOADING_STEPS_EN[loadingStepIndex]}
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h4 className="text-xs font-bold text-red-800 uppercase tracking-wider">
              {isUk ? "Помилка аналізу" : "Execution Error"}
            </h4>
            <p className="text-xs text-red-650 leading-relaxed font-mono">{error}</p>
          </div>
        </div>
      )}

      {/* Report View */}
      {report && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden">
          {/* Action Bar */}
          <div className="bg-slate-50 border-b border-slate-200 px-5 py-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <div className="bg-amber-50 p-1.5 rounded border border-amber-100">
                <FileText className="w-4 h-4 text-amber-600" />
              </div>
              <span className="text-xs font-bold text-slate-800 font-mono uppercase tracking-wider">
                {isUk ? "Інвестиційний звіт стартапу" : "Startup Investment Concept"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={handleCopyReport}
                className="py-1.5 px-3 bg-white hover:bg-slate-50 text-slate-650 hover:text-slate-800 border border-slate-200 rounded-lg text-[10px] font-bold font-mono uppercase tracking-wider flex items-center gap-1 cursor-pointer transition-colors shadow-xs"
              >
                {copiedReport ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedReport ? (isUk ? 'Скопійовано' : 'Copied') : (isUk ? 'Копіювати' : 'Copy')}</span>
              </button>
              <button 
                onClick={handleDownloadWord}
                className="py-1.5 px-3 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-lg text-[10px] font-bold font-mono uppercase tracking-wider flex items-center gap-1 cursor-pointer transition-colors shadow-xs"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Word (.docx)</span>
              </button>
            </div>
          </div>

          {/* Document Content */}
          <div className="p-6 sm:p-8 prose prose-slate max-w-none text-xs leading-relaxed font-mono bg-slate-50 border-b border-slate-100 overflow-x-auto whitespace-pre-wrap text-slate-800">
            {report}
          </div>
          
          <div className="p-4 bg-white flex justify-end">
            <button
              onClick={() => {
                setReport(null);
                setStartupIdea('');
                setProductDetails('');
                setFundingNeeds('');
              }}
              className="py-2 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold font-mono text-[10px] uppercase rounded-lg tracking-wider cursor-pointer transition-colors"
            >
              {isUk ? "Новий стартап" : "New Startup"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
